import { useCallback, useEffect, useRef, useState } from 'react';
import { listBots } from '../api/services/botService';
import { useWorkspace } from '../integrationQA/context/WorkspaceContext';

// Which bot Bot Configuration / Appearance & Branding are editing. This is a UI preference only
// (a bot id per workspace) — the bot record itself always lives behind the API.
const PREF_KEY = 'chatbizz_ui_active_bot';

function readPref() {
  try { return JSON.parse(localStorage.getItem(PREF_KEY) || '{}'); } catch (e) { return {}; }
}
function writePref(tenantId, botId) {
  try { localStorage.setItem(PREF_KEY, JSON.stringify({ ...readPref(), [tenantId]: botId })); } catch (e) { /* storage unavailable */ }
}

/**
 * Loads the workspace's bots and tracks the one being edited.
 * status: 'loading' | 'ready' | 'empty' | 'error'
 */
export default function useActiveBot() {
  const { tenantId } = useWorkspace();
  const [state, setState] = useState({ status: 'loading', bots: [], botId: null, error: null });
  const tenantRef = useRef(tenantId);
  tenantRef.current = tenantId;

  const load = useCallback(() => {
    let active = true;
    setState((s) => ({ ...s, status: 'loading', error: null }));
    listBots()
      .then((bots) => {
        if (!active) return;
        if (!bots.length) { setState({ status: 'empty', bots: [], botId: null, error: null }); return; }
        const preferred = readPref()[tenantRef.current];
        const chosen = bots.find((b) => b.id === preferred) || bots.find((b) => b.status === 'active') || bots[0];
        setState({ status: 'ready', bots, botId: chosen.id, error: null });
      })
      .catch((error) => { if (active) setState({ status: 'error', bots: [], botId: null, error }); });
    return () => { active = false; };
  }, []);

  useEffect(() => load(), [load, tenantId]);

  const setBotId = useCallback((id) => {
    writePref(tenantRef.current, id);
    setState((s) => ({ ...s, botId: id }));
  }, []);

  /** Replaces a bot in the local list after the server returned its updated record. */
  const replaceBot = useCallback((updated) => {
    setState((s) => ({ ...s, bots: s.bots.map((b) => (b.id === updated.id ? updated : b)) }));
  }, []);

  const bot = state.bots.find((b) => b.id === state.botId) || null;
  return { ...state, bot, setBotId, replaceBot, reload: load };
}
